import { Component, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';

export interface CycleTimeRapor {
  toplamSure: number;
  toplamSureStr: string;
  islemeSure: number;
  islemeSureStr: string;
  yaklasma: number;
  yaklasmaStr: string;
  uzaklasma: number;
  uzaklasmaStr: string;
  bosta: number;
  bostaStr: string;
  islemeYuzde: number;
  yaklasmYuzde: number;
  uzaklasmaYuzde: number;
  bostaYuzde: number;
  verimlilik: number;
  toplamHareket: number;
  islemeHareket: number;
  toplamMesafe: number;
  islemeMesafe: number;
  oneriler: string[];
}

export interface SimulasyonNokta {
  x: number;
  y: number;
  z: number;
  hiz?: number;
  hareketTuru?: string;
  renk?: THREE.Color;
}

@Component({
  selector: 'fiko-cycle-time-rapor',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './fiko-cycle-time-rapor.component.html',
  styleUrls: ['./fiko-cycle-time-rapor.component.less']
})
export class FikoCycleTimeRaporComponent {

  @Input() noktalar: SimulasyonNokta[] = [];
  @Input() reelIlerleme: number = 100;
  @Input() popoverId: string = 'cycle-time-popup';

  /** Renk referansları (hareket türü tespiti için) */
  @Input() renkIsleme: THREE.Color = new THREE.Color('chartreuse');
  @Input() renkYaklasma: THREE.Color = new THREE.Color('yellow');
  @Input() renkUzaklasma: THREE.Color = new THREE.Color('blue');
  @Input() renkBosta: THREE.Color = new THREE.Color('red');

  @Output() raporHazir = new EventEmitter<CycleTimeRapor>();
  @Output() kopyalandi = new EventEmitter<void>();

  rapor: CycleTimeRapor = null;

  constructor(private cd: ChangeDetectorRef) {}

  /** Hareket türünü al (string veya renk karşılaştırması) */
  private hareketTuruAl(nokta: SimulasyonNokta): string {
    // Önce string hareketTuru kontrol et
    if (nokta?.hareketTuru) return nokta.hareketTuru;

    // Renk karşılaştırması yap
    if (nokta?.renk) {
      if (nokta.renk.equals?.(this.renkIsleme)) return 'İşleme';
      if (nokta.renk.equals?.(this.renkYaklasma)) return 'Yaklaşma';
      if (nokta.renk.equals?.(this.renkUzaklasma)) return 'Uzaklaşma';
      if (nokta.renk.equals?.(this.renkBosta)) return 'Boşta';
    }

    // Varsayılan: İşleme (simülasyon component'i ile tutarlı)
    return 'İşleme';
  }

  /** Cycle Time analizi hesapla - tüm noktaları tarar */
  analizHesapla() {
    if (!this.noktalar || this.noktalar.length < 2) {
      this.rapor = null;
      return;
    }

    let islemeSure = 0;
    let yaklasma = 0;
    let uzaklasma = 0;
    let bosta = 0;
    let islemeHareket = 0;
    let islemeMesafe = 0;
    let toplamMesafe = 0;

    const reelCarpan = this.reelIlerleme / 100;

    for (let i = 1; i < this.noktalar.length; i++) {
      const onceki = this.noktalar[i - 1];
      const simdiki = this.noktalar[i];

      // Mesafe hesapla
      const dx = simdiki.x - onceki.x;
      const dy = simdiki.y - onceki.y;
      const dz = simdiki.z - onceki.z;
      const mesafe = Math.sqrt(dx * dx + dy * dy + dz * dz);
      toplamMesafe += mesafe;

      // Süre hesapla (hız mm/dk, mesafe mm)
      const hiz = simdiki.hiz || 1000; // varsayılan 1000 mm/dk
      const sure = (mesafe / hiz) * 60; // saniye cinsinden

      // Hareket türüne göre kategorize et
      const tur = this.hareketTuruAl(simdiki);
      switch (tur) {
        case 'İşleme':
        case 'ISLEME':
        case 'Isleme':
          islemeSure += sure;
          islemeHareket++;
          islemeMesafe += mesafe;
          break;
        case 'Yaklaşma':
        case 'YAKLASMA':
        case 'Yaklasma':
          yaklasma += sure;
          break;
        case 'Uzaklaşma':
        case 'UZAKLASMA':
        case 'Uzaklasma':
          uzaklasma += sure;
          break;
        default:
          bosta += sure;
          break;
      }
    }

    // Reel ilerleme ile çarp
    islemeSure /= reelCarpan;
    yaklasma /= reelCarpan;
    uzaklasma /= reelCarpan;
    bosta /= reelCarpan;

    const toplamSure = islemeSure + yaklasma + uzaklasma + bosta;
    const verimlilik = toplamSure > 0 ? (islemeSure / toplamSure) * 100 : 0;

    // İyileştirme önerileri
    const oneriler: string[] = [];

    const bostaYuzde = toplamSure > 0 ? (bosta / toplamSure) * 100 : 0;
    const yaklasmYuzde = toplamSure > 0 ? (yaklasma / toplamSure) * 100 : 0;
    const uzaklasmaYuzde = toplamSure > 0 ? (uzaklasma / toplamSure) * 100 : 0;

    if (bostaYuzde > 20) {
      oneriler.push(`Boşta hareket %${bostaYuzde.toFixed(0)} - Rapid hızını artırarak veya yol optimizasyonu ile azaltılabilir.`);
    }
    if (yaklasmYuzde > 15) {
      oneriler.push(`Yaklaşma süresi %${yaklasmYuzde.toFixed(0)} - Takım yolu yaklaşma stratejisi optimize edilebilir.`);
    }
    if (uzaklasmaYuzde > 15) {
      oneriler.push(`Uzaklaşma süresi %${uzaklasmaYuzde.toFixed(0)} - Retract mesafeleri gözden geçirilebilir.`);
    }
    if (verimlilik < 50) {
      oneriler.push(`Verimlilik %${verimlilik.toFixed(0)} - Toplam cycle time'ın yarısından azı işleme. CAM stratejisi optimize edilmeli.`);
    }
    if (verimlilik >= 70) {
      oneriler.push(`✓ İyi verimlilik! İşleme oranı %${verimlilik.toFixed(0)} - Program optimize görünüyor.`);
    }

    // Ortalama ilerleme hızı analizi
    const ortalamaIslemeHizi = islemeHareket > 0 ? (islemeMesafe / islemeSure) * 60 : 0; // mm/dk
    if (ortalamaIslemeHizi < 500 && islemeHareket > 10) {
      oneriler.push(`Ortalama işleme hızı ${ortalamaIslemeHizi.toFixed(0)} mm/dk - Feeds & Speeds optimize edilebilir.`);
    }

    this.rapor = {
      toplamSure,
      toplamSureStr: this.sureFormatla(toplamSure),
      islemeSure,
      islemeSureStr: this.sureFormatla(islemeSure),
      yaklasma,
      yaklasmaStr: this.sureFormatla(yaklasma),
      uzaklasma,
      uzaklasmaStr: this.sureFormatla(uzaklasma),
      bosta,
      bostaStr: this.sureFormatla(bosta),
      islemeYuzde: toplamSure > 0 ? (islemeSure / toplamSure) * 100 : 0,
      yaklasmYuzde,
      uzaklasmaYuzde,
      bostaYuzde,
      verimlilik,
      toplamHareket: this.noktalar.length - 1,
      islemeHareket,
      toplamMesafe,
      islemeMesafe,
      oneriler
    };

    this.raporHazir.emit(this.rapor);
    this.cd.detectChanges();
  }

  /** Süreyi okunabilir formata çevir (sn → dk:sn veya sa:dk:sn) */
  private sureFormatla(saniye: number): string {
    if (saniye < 60) {
      return `${saniye.toFixed(1)} sn`;
    } else if (saniye < 3600) {
      const dk = Math.floor(saniye / 60);
      const sn = Math.floor(saniye % 60);
      return `${dk}dk ${sn}sn`;
    } else {
      const sa = Math.floor(saniye / 3600);
      const dk = Math.floor((saniye % 3600) / 60);
      const sn = Math.floor(saniye % 60);
      return `${sa}sa ${dk}dk ${sn}sn`;
    }
  }

  /** Raporu panoya kopyala (metin formatında) */
  raporuKopyala() {
    if (!this.rapor) return;

    const r = this.rapor;
    const metin = `
═══════════════════════════════════════
       CYCLE TIME ANALİZ RAPORU
═══════════════════════════════════════

ÖZET
────────────────────────────────────────
Toplam Süre:     ${r.toplamSureStr}
İşleme Süresi:   ${r.islemeSureStr} (${r.islemeYuzde.toFixed(1)}%)
Yaklaşma:        ${r.yaklasmaStr} (${r.yaklasmYuzde.toFixed(1)}%)
Uzaklaşma:       ${r.uzaklasmaStr} (${r.uzaklasmaYuzde.toFixed(1)}%)
Boşta (Rapid):   ${r.bostaStr} (${r.bostaYuzde.toFixed(1)}%)

HAREKET İSTATİSTİKLERİ
────────────────────────────────────────
Toplam Hareket:    ${r.toplamHareket}
İşleme Hareketi:   ${r.islemeHareket}
Toplam Mesafe:     ${r.toplamMesafe.toFixed(1)} mm
İşleme Mesafesi:   ${r.islemeMesafe.toFixed(1)} mm

VERİMLİLİK SKORU: ${r.verimlilik.toFixed(0)}%
${r.verimlilik >= 70 ? '★★★ Mükemmel' : r.verimlilik >= 50 ? '★★☆ Orta' : '★☆☆ Düşük'}

${r.oneriler.length > 0 ? 'İYİLEŞTİRME ÖNERİLERİ\n────────────────────────────────────────\n' + r.oneriler.map((o, i) => `${i + 1}. ${o}`).join('\n') : ''}

═══════════════════════════════════════
Reel İlerleme: %${this.reelIlerleme} ile hesaplandı
FikoYT CNC Simülasyon Sistemi
═══════════════════════════════════════
`.trim();

    navigator.clipboard.writeText(metin).then(() => {
      this.kopyalandi.emit();
      console.log('Cycle Time raporu panoya kopyalandı');
    });
  }

  /** Verimlilik rengini al */
  getVerimlilikRenk(): string {
    if (!this.rapor) return '#888';
    if (this.rapor.verimlilik >= 70) return '#0f8';
    if (this.rapor.verimlilik >= 50) return '#ff0';
    return '#f44';
  }

  /** Verimlilik gradient'ını al */
  getVerimlilikGradient(): string {
    if (!this.rapor) return 'linear-gradient(90deg,#888,#888)';
    if (this.rapor.verimlilik >= 70) return 'linear-gradient(90deg,#0a6,#0f8)';
    if (this.rapor.verimlilik >= 50) return 'linear-gradient(90deg,#a80,#ff0)';
    return 'linear-gradient(90deg,#a00,#f44)';
  }
}
